/**
 * MobileBottomNav — IFT main-site bottom tab bar.
 *
 * Renders on mobile (<768px) across every IFT main-site route EXCEPT:
 *   • /m/* routes (the deep PWA shell owns its own bottom bar)
 *   • Auth pages (/login, /register, /sign-in, /auth)
 *   • Immersive / print / hosted-site modes
 *
 * Why this exists:
 *   Promotional traffic lands on /, /family/*, /celebrate, etc. — not /m/*.
 *   Giving mobile visitors a persistent 5-tab bar makes the site feel like
 *   an app from the first second, which is the most important thing when
 *   promoting IFT in India.
 *
 * Design: Saffron-ink minimal bar with safe-area padding for iPhone.
 */
import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, TreePine, Users, Camera, User } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { resolveSiteSlug } from "@/config/siteConfig";

const P = {
  ink: "#431a04",
  inkSoft: "#8a5a2e",
  saffron: "#C2410C",
  saffronBg: "#FFF7ED",
  line: "rgba(194,65,12,0.14)",
};

function isHiddenRoute(pathname) {
  if (!pathname) return false;
  if (pathname.startsWith("/m/") || pathname === "/m") return true;
  if (pathname.startsWith("/login") || pathname.startsWith("/register")) return true;
  if (pathname.startsWith("/sign-in") || pathname.startsWith("/auth")) return true;
  if (pathname.startsWith("/print/") || pathname.includes("/print")) return true;
  if (pathname.startsWith("/share/")) return true; // immersive invite view
  if (pathname.startsWith("/admin/memorials/quick")) return true; // standalone admin sign-in
  return false;
}

export default function MobileBottomNav() {
  const location = useLocation();
  const auth = useAuth();
  const user = auth ? auth.user : null;

  const siteSlug = resolveSiteSlug();
  const hiddenRoute = isHiddenRoute(location.pathname);

  const tabs = useMemo(() => {
    const treeHref =
      user && user.primary_tree_slug
        ? `/family/${user.primary_tree_slug}`
        : "/family-trees";
    const familyHref = user ? "/dashboard" : "/family-trees";
    const profileHref = user ? "/profile" : "/login";
    return [
      { to: "/",            Icon: Home,     label: "Home",     testid: "nav-home" },
      { to: treeHref,       Icon: TreePine, label: "Tree",     testid: "nav-tree" },
      { to: familyHref,     Icon: Users,    label: "Family",   testid: "nav-family" },
      { to: "/celebrate",   Icon: Camera,   label: "Memories", testid: "nav-memories" },
      { to: profileHref,    Icon: User,     label: "Profile",  testid: "nav-profile" },
    ];
  }, [user]);

  // Only on IFT host; foodpulse / socialpulse get their own navigation.
  if (siteSlug !== "indianfamilytree") return null;
  if (hiddenRoute) return null;

  const isActive = (href) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname === href || location.pathname.startsWith(href + "/");
  };

  return (
    <>
      {/* Spacer so page content doesn't hide behind the fixed bar */}
      <div aria-hidden="true" className="md:hidden" style={{ height: "calc(68px + env(safe-area-inset-bottom, 0px))" }} />
      <nav
        data-testid="mobile-bottom-nav"
        className="md:hidden fixed bottom-0 left-0 right-0 z-40"
        style={{
          background: "rgba(255,247,237,0.98)",
          borderTop: `1px solid ${P.line}`,
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          boxShadow: "0 -10px 30px -16px rgba(194,65,12,0.22)",
        }}
        role="navigation"
        aria-label="Primary"
      >
        <div className="grid grid-cols-5">
          {tabs.map(({ to, Icon, label, testid }) => {
            const active = isActive(to);
            return (
              <Link
                key={testid}
                to={to}
                data-testid={testid}
                aria-current={active ? "page" : undefined}
                className="flex flex-col items-center justify-center gap-0.5 py-2.5 text-[11px] font-medium transition-colors"
                style={{
                  color: active ? P.saffron : P.inkSoft,
                }}
              >
                <Icon
                  className="w-5 h-5"
                  strokeWidth={active ? 2.4 : 1.8}
                  aria-hidden="true"
                />
                <span style={{ fontWeight: active ? 700 : 500 }}>{label}</span>
                {active && (
                  <span
                    aria-hidden="true"
                    style={{
                      display: "block",
                      width: 20,
                      height: 2,
                      borderRadius: 2,
                      marginTop: 2,
                      background: P.saffron,
                    }}
                  />
                )}
              </Link>
            );
          })}
        </div>
      </nav>
    </>
  );
}
